"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createSaiaStrategy = createSaiaStrategy;
const constants_1 = require("../constants");
const validationHelpers_1 = require("../common/validationHelpers");
const DEFAULT_SAIA_MAX_LENGTH = 12;
function createSaiaStrategy(options) {
    const { enableCheckDigit } = options;
    const maxLength = DEFAULT_SAIA_MAX_LENGTH;
    const getCheckDigit = (proId) => {
        if (isNaN(Number(proId))) {
            return 0; // fallback for non-numeric input
        }
        const length = proId.length;
        const is700Prefix = proId.startsWith("700");
        // OVLD: 700 prefix + 11 or 12 digits → check digit is always 0
        if (is700Prefix && (length === 11 || length === 12)) {
            return 0;
        }
        // Pad to 11 digits before calculating check digit
        const padded = length < 11 ? proId.padStart(11, "0") : proId;
        const firstTen = Number(padded.slice(0, 10));
        return firstTen % 7; // final digit (0–6)
    };
    const getProNumberWithCheckDigit = ({ proNumber, }) => {
        const length = proNumber.length;
        const is700Prefix = proNumber.startsWith("700");
        // Special rule: 700 prefix + 10 digits → append "00"
        if (is700Prefix && length === 10) {
            return proNumber + "00";
        }
        // Standard case: add 1-digit check digit when missing
        if (enableCheckDigit && length === maxLength - 1) {
            return proNumber + getCheckDigit(proNumber).toString();
        }
        return proNumber;
    };
    const validateProNumber = (validationInput) => {
        const { proNumber, isProPreAssigned } = validationInput;
        const actualLength = proNumber.length;
        const is700Prefix = proNumber.startsWith("700");
        const hasLeadingZeros = proNumber.startsWith("0");
        const digitsWithoutLeadingZeros = proNumber.replace(/^0+/, "");
        // Too long → invalid
        if (actualLength > maxLength) {
            return (0, validationHelpers_1.createValidationError)(constants_1.PRO_VALIDATION_ERROR_TYPES.PRO_NUMBER_MAX, {
                maxLength,
            });
        }
        // Pre-assigned OR 700 prefix with < 11 digits are always allowed
        if (isProPreAssigned || (is700Prefix && actualLength < 11)) {
            return null;
        }
        // Validate lengths ≤ 11
        if (actualLength <= 11) {
            // Shorter than 11 digits OR starts with 0 → must have ≥ 9 real digits
            if (actualLength < 11 || hasLeadingZeros) {
                if (digitsWithoutLeadingZeros.length < 9) {
                    return (0, validationHelpers_1.createValidationError)(constants_1.PRO_VALIDATION_ERROR_TYPES.INVALID_PRO_NUMBER);
                }
                return null;
            }
            // Exactly 11 digits and no leading zeros → missing check digit
            return (0, validationHelpers_1.createValidationError)(constants_1.PRO_VALIDATION_ERROR_TYPES.PRO_NUMBER_MIN, {
                minLength: maxLength,
            });
        }
        // Exactly 12 digits → validate content + check digit
        if (actualLength === maxLength) {
            // Must contain ≥ 10 real digits
            if (digitsWithoutLeadingZeros.length < 10) {
                return (0, validationHelpers_1.createValidationError)(constants_1.PRO_VALIDATION_ERROR_TYPES.INVALID_PRO_NUMBER);
            }
            // Check digit validation
            if (enableCheckDigit) {
                const entered = Number(proNumber.slice(-1)); // last digit
                const expected = getCheckDigit(proNumber.slice(0, maxLength - 1)); // compute expected
                if (entered !== expected) {
                    return (0, validationHelpers_1.createValidationError)(constants_1.PRO_VALIDATION_ERROR_TYPES.INVALID_CHECK_DIGIT);
                }
            }
            return null;
        }
        return null;
    };
    const isTempProNumber = (proNumber) => {
        return proNumber.startsWith("888"); // SAIA temp pro range
    };
    return {
        getProNumberWithCheckDigit,
        validateProNumber,
        isTempProNumber,
        getCheckDigit,
    };
}
